import Collapse from '@material-ui/core/Collapse';
import React, { useEffect, useState } from 'react';
import useCurrentLocation from '../../../hooks/useCurrentLocation';
import { PortalMenuItem } from '../../../PortalNavigation.interfaces';
import { withNavigationDropdownDivider } from '../../dropdown/NavigationDropdownDivider';
import MenuItem from '@/features/PortalNavigation/variants/dropdown/MenuItem/MenuItem';
import { useNavigationCollapseStyles } from '@/features/PortalNavigation/variants/mobile/NavigationCollapse/NavigationCollapse.styles';

const NavigationDropdownItemWithDivider = withNavigationDropdownDivider(MenuItem);

interface NavigationMobileDropdownProps {
  isOpen: boolean;
  navigationItems: PortalMenuItem[];
  onNavigationItemClick: () => void;
  'data-datalayer-position'?: string;
  'data-datalayer-variant'?: string;
}

const NavigationCollapse = ({
  isOpen,
  navigationItems,
  onNavigationItemClick,
  'data-datalayer-position': datalayerPosition,
  'data-datalayer-variant': datalayerVariant,
}: NavigationMobileDropdownProps) => {
  const classes = useNavigationCollapseStyles();
  const [subItemSelectedIndex, setSubItemSelectedIndex] = useState<number | null>(null);
  const { hasChildrenWithCurrentLocation, isCurrentLocation } = useCurrentLocation();

  const handleSubItemClick = React.useCallback(
    (itemIndex: number) => () => {
      setSubItemSelectedIndex((prevIndex) => {
        if (prevIndex === itemIndex) return null;
        return itemIndex;
      });
    },
    [],
  );

  useEffect(() => {
    if (!isOpen) {
      // closing sub menus when the main menu is closed.
      setSubItemSelectedIndex(null);
    }
  }, [isOpen]);

  const isNavigationIndexOpen = (index: number) => subItemSelectedIndex === index;

  return (
    <Collapse in={isOpen}>
      <div
        className={classes.navigationDropdownContent}
        data-datalayer-position={datalayerPosition}
        data-datalayer-variant={datalayerVariant}
      >
        {navigationItems.map((item, index) => (
          <NavigationDropdownItemWithDivider
            key={index}
            navigationItem={item}
            onItemClick={item?.navigationItems ? handleSubItemClick(index) : onNavigationItemClick}
            arrowIconDirection={isNavigationIndexOpen(index) ? 'top' : 'bottom'}
            isActive={
              isCurrentLocation(item) || hasChildrenWithCurrentLocation(item?.navigationItems)
            }
            hoverDisabled
          >
            {item?.navigationItems && (
              <Collapse in={isNavigationIndexOpen(index)} className={classes.subMenuWrapper}>
                <div className="sub-menu-content">
                  {item?.navigationItems.map((subItem, subIndex) => (
                    <NavigationDropdownItemWithDivider
                      key={subIndex}
                      navigationItem={subItem}
                      onItemClick={onNavigationItemClick}
                      hoverDisabled
                      isActive={isCurrentLocation(subItem)}
                    />
                  ))}
                </div>
              </Collapse>
            )}
          </NavigationDropdownItemWithDivider>
        ))}
      </div>
    </Collapse>
  );
};

export default React.memo(NavigationCollapse);
